import React, { useCallback, useEffect, useState } from 'react'
import { Button, Switch } from 'antd'
import { useHistory } from 'react-router-dom'
import { BodyText } from 'src/branding/typography'
import styled from 'styled-components/macro'
import SuccessHubBusinessDaysCard from 'src/components/growthEngine/SuccessHubBusinessDaysCard'
import {
  fetchGrowthEngineSuccessHubSettings,
  postGrowthEngineSuccessHubPrefs,
} from 'src/utils/growthEngineApi'
import {
  PROGRESS_DISCOVERY_SETTINGS_BODY,
  PROGRESS_DISCOVERY_SETTINGS_LABEL,
  getVenueProgressPath,
} from 'src/utils/growthEngineProgressDiscovery'

/**
 * Settings home for streaks (venue kitchen days) and job-note prefs.
 * Suggests order weekdays — never auto-enables. Celebrations-off is honoured.
 * Quiet Progress link for people already in prefs.
 */
const SuccessHubSettings = ({ venueId }) => {
  const history = useHistory()
  const [loadState, setLoadState] = useState('idle')
  const [busy, setBusy] = useState(false)
  const [prefs, setPrefs] = useState({ celebrations: { enabled: true } })
  const [businessDays, setBusinessDays] = useState(null)
  const progressPath = getVenueProgressPath(venueId)

  const loadSettings = useCallback(async () => {
    if (!venueId) return
    setLoadState('loading')
    try {
      const next = await fetchGrowthEngineSuccessHubSettings(venueId)
      setPrefs(next.prefs || { celebrations: { enabled: true } })
      setBusinessDays(next.businessDays || null)
      setLoadState('ready')
    } catch (err) {
      console.warn('Progress settings failed to load', err)
      setLoadState('error')
    }
  }, [venueId])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const toggleCelebrations = async checked => {
    if (!venueId || busy) return
    const enabled = Boolean(checked)
    const previous = prefs
    setPrefs({ celebrations: { enabled } })
    setBusy(true)
    try {
      const next = await postGrowthEngineSuccessHubPrefs(venueId, {
        celebrations: { enabled },
      })
      setPrefs(next.prefs || { celebrations: { enabled } })
    } catch (err) {
      console.warn('Celebrations pref failed to save', err)
      setPrefs(previous)
    }
    setBusy(false)
  }

  if (!venueId) return null

  return (
    <Wrap data-testid='success-hub-settings'>
      {loadState === 'error' ? (
        <BodyText>
          Couldn’t load kitchen days.{' '}
          <Button
            type='link'
            onClick={loadSettings}
            data-testid='settings-hub-retry'
          >
            Try again
          </Button>
        </BodyText>
      ) : loadState !== 'ready' ? (
        <BodyText>Loading kitchen days…</BodyText>
      ) : (
        <>
          <SuccessHubBusinessDaysCard
            venueId={venueId}
            businessDays={businessDays}
            onBusinessDaysChange={setBusinessDays}
            testIdPrefix='settings'
          />

          <Notes data-testid='settings-celebrations'>
            <NotesCopy>
              <Title>Short notes when you finish a job</Title>
              <Body>
                Progress can show a quiet receipt after a live unlock. Turn this
                off if you’d rather keep the board still.
              </Body>
            </NotesCopy>
            <Switch
              checked={prefs?.celebrations?.enabled !== false}
              onChange={toggleCelebrations}
              disabled={busy}
              data-testid='settings-celebrations-toggle'
            />
          </Notes>

          {progressPath ? (
            <ProgressInvite data-testid='settings-progress-invite'>
              <NotesCopy>
                <Title>Success Hub</Title>
                <Body>{PROGRESS_DISCOVERY_SETTINGS_BODY}</Body>
              </NotesCopy>
              <Button
                type='default'
                onClick={() => history.push(progressPath)}
                data-testid='settings-progress-open'
              >
                {PROGRESS_DISCOVERY_SETTINGS_LABEL}
              </Button>
            </ProgressInvite>
          ) : null}
        </>
      )}
    </Wrap>
  )
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: ${({ theme }) => theme?.spacing?.md || '20px'};
`

const Title = styled.h4`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const Body = styled(BodyText)`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`

const Notes = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`

const NotesCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const ProgressInvite = styled.div`
  align-items: center;
  background: ${({ theme }) => theme?.colours?.mayoWhite || '#fff'};
  border: 1px solid
    ${({ theme }) => theme?.colours?.chipGrey || 'rgba(0, 0, 0, 0.06)'};
  border-radius: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  justify-content: space-between;
  padding: 14px 16px;
`

export default SuccessHubSettings
